import React from "react";
import { useSnapshot } from "valtio";
import { Card } from "reactstrap";
import { designStore, generalConfigStore } from "@store";

import { getImageContentById } from "@helpers/general";

import {
    CATEGORY_DISPLAY_MODE_1,
    CATEGORY_SVG_BORDER_WIDTH,
    CATEGORY_SVG_BORDER_SLICE,
} from "@constants/index";

import { data } from "@pages/GeneralConfig/Design/Content/OrderTaking/fastFoodCard";
import {
    AddIcon,
    AllergenIcon,
    FireIcon,
    InfoIcon,
} from "@pages/GeneralConfig/Design/Preview/OrderTaking/Icons";
import { DisplayStyleValues } from "@pages/GeneralConfig/Design/Content/OrderTaking/GenericInterface/Design/shared/DisplayStyleSettings";

import {
    makeResolveColor,
    resolveProductDisplayStyle,
    DEFAULT_DISPLAY_TEXT_COLOR,
} from "./displayStyleHelpers";

import "./category.css";
import "./index.css";

export function CardWrapper(): JSX.Element {
    const {
        selectedCategory,
        isProductTitleActive,
        isProductDescriptionActive,
        productAccessIcon,
        calorieIcon,
        productDisplayMode,
        productDisplayBackgroundColor,
        productDisplayBackgroundOpacity,
        productDisplaySelectedTextColor,
        productDisplayBorderColor,
        productDisplayBorderStyle,
        productDisplaySvgBorder,
        productDisplaySvgBorderName,
        productDisplayMode1BackgroundColor,
        productDisplayMode1BackgroundOpacity,
        productDisplayMode1SelectedTextColor,
        productDisplayMode1BorderColor,
        productDisplayMode1BorderStyle,
        productDisplayMode1SvgBorder,
        productDisplayMode1SvgBorderName,
    } = useSnapshot(designStore);
    const {
        project: {
            files: images,
            template: {
                content: { generalDesign },
            },
        },
        secondColor,
        isAllergenOrderTakingActive,
        isOrderTakingCaloryActive,
        isOrderTakingProductInformationActive,
        isBorderCardProductActive,
        isNutriscoreOnTheProductActive,
        isCoverImageActive,
    } = useSnapshot(generalConfigStore);

    const product = data.products.filter(
        (product) => product.category === selectedCategory.name
    )[0];

    const productAccessIconContent = getImageContentById(
        productAccessIcon.id,
        images
    );
    const imageSrc =
        productAccessIconContent?.includes("http") ||
        productAccessIconContent?.includes("https")
            ? productAccessIconContent
            : `./images/${productAccessIconContent}`;
    const calorieIconContent = getImageContentById(calorieIcon.id, images);
    const imageSrcCalorieIcon =
        calorieIconContent?.includes("http") ||
        calorieIconContent?.includes("https")
            ? calorieIconContent
            : `./images/${calorieIconContent}`;

    // Styles produit configurables (ESS-3856) : fond + opacité, couleur texte,
    // bordure couleur uni / SVG. Chaque mode (Mode 1 / Mode 2) a ses propres
    // réglages indépendants ; on applique ceux du mode actif, dans les DEUX
    // modes (le mode ne masque plus le style — cf. ticket "applicable en
    // Mode 1 et Mode 2").
    const isProductMode1 = productDisplayMode === CATEGORY_DISPLAY_MODE_1;
    // Jeu de valeurs du mode actif (Mode 1 / Mode 2), chaque mode étant
    // indépendant. La résolution couleur/fond/bordure est centralisée dans
    // resolveProductDisplayStyle (source unique partagée avec MainContentWrapper).
    const activeValues: DisplayStyleValues = isProductMode1
        ? {
              backgroundColor: productDisplayMode1BackgroundColor,
              backgroundOpacity: productDisplayMode1BackgroundOpacity,
              selectedTextColor: productDisplayMode1SelectedTextColor,
              borderColor: productDisplayMode1BorderColor,
              borderStyle: productDisplayMode1BorderStyle,
              svgBorder: productDisplayMode1SvgBorder,
              svgBorderName: productDisplayMode1SvgBorderName,
          }
        : {
              backgroundColor: productDisplayBackgroundColor,
              backgroundOpacity: productDisplayBackgroundOpacity,
              selectedTextColor: productDisplaySelectedTextColor,
              borderColor: productDisplayBorderColor,
              borderStyle: productDisplayBorderStyle,
              svgBorder: productDisplaySvgBorder,
              svgBorderName: productDisplaySvgBorderName,
          };
    const resolveColor = makeResolveColor(generalDesign as GeneralDesignType);
    const {
        background: productBackground,
        textColor: resolvedTextColor,
        resolvedBorderColor: activeBorderColor,
        safeSvgBorderUrl,
        isUniBorder: isProductUniBorder,
        isSvgBorder: isProductSvgBorder,
    } = resolveProductDisplayStyle(activeValues, resolveColor);
    const productTextColor = resolvedTextColor || DEFAULT_DISPLAY_TEXT_COLOR;

    return (
        <div className="d-flex justify-content-center align-items-center w-100 h-100">
            <div
                style={{
                    position: "relative",
                    width: "200px",
                    height: "200px",
                }}
            >
                {/* Bordure SVG (border-image, au-dessus de la carte) */}
                {isProductSvgBorder ? (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            borderStyle: "solid",
                            borderWidth: `${CATEGORY_SVG_BORDER_WIDTH}px`,
                            borderImage: `url(${safeSvgBorderUrl}) ${CATEGORY_SVG_BORDER_SLICE} stretch`,
                            pointerEvents: "none",
                            zIndex: 3,
                        }}
                    />
                ) : null}
                {/* Bordure couleur uni (au-dessus de la carte) */}
                {isProductUniBorder ? (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            border: `2px solid ${activeBorderColor}`,
                            borderRadius: "9px",
                            pointerEvents: "none",
                            zIndex: 3,
                        }}
                    />
                ) : null}
                <Card
                    style={{
                        height: "200px",
                        width: "200px",
                        display: "grid",
                        gridTemplateRows: "100px 18px 20px 20px 30px",
                        gap: "3px",
                        border:
                            isBorderCardProductActive === true ? "" : "unset",
                        background: productBackground,
                    }}
                >
                    <div
                        className="d-flex flex-row justify-content-around align-items-center"
                        style={{ gap: "10px" }}
                    >
                        <div className="d-flex justify-content-center align-items-center  h-100 w-100">
                            <img
                                alt={product.name.content}
                                src={product.image}
                                style={{
                                    height: "90%",
                                    width: "80%",
                                    objectFit: isCoverImageActive
                                        ? "cover"
                                        : "contain",
                                }}
                            />
                        </div>
                        <div
                            className="mt-2 d-flex flex-column justify-content-end align-items-center"
                            style={{
                                gap:
                                    isOrderTakingProductInformationActive ===
                                    true
                                        ? "3px"
                                        : "9px",
                            }}
                        >
                            {isOrderTakingProductInformationActive === true ? (
                                productAccessIconContent !== "" ? (
                                    <img
                                        alt="productAccessIcon"
                                        src={imageSrc}
                                        style={{
                                            height: "20px",
                                            width: "23px",
                                        }}
                                    />
                                ) : (
                                    <InfoIcon />
                                )
                            ) : null}

                            {isAllergenOrderTakingActive === true ? (
                                <AllergenIcon
                                    globalColor={
                                        (generalDesign as GeneralDesignType)
                                            .colors[0].content
                                    }
                                />
                            ) : null}

                            <AddIcon
                                globalColor={
                                    (generalDesign as GeneralDesignType)
                                        .colors[0].content
                                }
                                secondColor={secondColor}
                            />
                        </div>
                    </div>
                    {isOrderTakingCaloryActive === true ||
                    isNutriscoreOnTheProductActive === true ? (
                        <div
                            className="d-flex justify-content-center align-items-center"
                            style={{ gap: "10px" }}
                        >
                            {isOrderTakingCaloryActive === true ? (
                                <div
                                    className="d-flex justify-content-center align-items-center"
                                    style={{
                                        gap: "2px",
                                    }}
                                >
                                    {calorieIconContent !== "" ? (
                                        <img
                                            alt="calorieIcon"
                                            src={imageSrcCalorieIcon}
                                            style={{
                                                height: "20px",
                                                width: "23px",
                                            }}
                                        />
                                    ) : (
                                        <FireIcon />
                                    )}

                                    <span
                                        style={{
                                            fontWeight: "normal",
                                            fontSize: "10px",
                                            fontFamily: "Segoe UI",
                                            textAlign: "center",
                                            marginTop: "5px",
                                        }}
                                    >
                                        {product.calorie.content} {"cal"}
                                    </span>
                                </div>
                            ) : null}
                            {isNutriscoreOnTheProductActive ? (
                                <div className="d-flex justify-content-center align-items-center">
                                    <img
                                        alt="Nutri-Score"
                                        src="./images/c.png"
                                        style={{
                                            height: "20px",
                                            width: "23px",
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <div></div>
                    )}
                    {isProductTitleActive === true ? (
                        <span
                            className="mt-1 d-flex justify-content-center align-items-center"
                            style={{
                                font: "normal normal bold 9px/5px Segoe UI",
                                textAlign: "center",
                                width: "100%",
                                height: "100%",
                                color: productTextColor,
                                textTransform: "uppercase",
                            }}
                        >
                            {product.name.content}
                        </span>
                    ) : (
                        <div></div>
                    )}
                    <div
                        className="d-flex justify-content-center align-items-center"
                        style={{
                            textAlign: "center",
                            width: "100%",
                            height: "100%",
                            color: productTextColor,
                            textTransform: "uppercase",
                        }}
                    >
                        {product.price.content} {"€"}
                    </div>

                    {isProductDescriptionActive ? (
                        <span
                            style={{
                                textAlign: "center",
                                color: productTextColor,
                                fontWeight: "normal",
                                fontSize: "9px",
                                fontFamily: "Segoe UI",
                            }}
                        >
                            {product.description.content}
                        </span>
                    ) : null}
                </Card>
            </div>
        </div>
    );
}
